/*
 * Tooltip — the label of an icon-only action, in the app's own voice.
 *
 * Deliberately the same object as the InfoHint bubble: same surface, same radius, same
 * shadow, same caret. One of them explains a field and the other names a control, but a
 * reader meeting both should not think the product has two kinds of popup.
 *
 * It carries no accent hairline, though — that gesture belongs to the hint, which is
 * something you asked for; a tooltip is something that arrives on its own.
 */

.tooltip {
  position: fixed;
  z-index: var(--z-tooltip);
  max-inline-size: 240px;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--bg-elevated);
  color: var(--text);
  box-shadow: var(--shadow-lg);
  font-size: var(--text-xs);
  line-height: var(--leading-tight);
  text-align: center;
  white-space: nowrap;
  /* It follows the pointer's target; it must never become the target itself. */
  pointer-events: none;
  animation: tooltip-in var(--transition-fast);
}

/* `left` / `top` are the control's centre, set inline; the transform centres the bubble on
   that point and seats it above or below. Direction-neutral, so RTL needs no rule. */
.tooltip--above {
  transform: translate(-50%, calc(-100% - 8px));
  transform-origin: bottom center;
}

.tooltip--below {
  transform: translate(-50%, 8px);
  transform-origin: top center;
}

/* The caret: a rotated square borrowing the bubble's own border and surface, so it stays
   correct without a second colour to keep in sync. */
.tooltip::after {
  content: '';
  position: absolute;
  inset-inline-start: 50%;
  inline-size: 8px;
  block-size: 8px;
  margin-inline-start: -4px;
  background: var(--bg-elevated);
  rotate: 45deg;
}

.tooltip--above::after {
  inset-block-end: -5px;
  border-inline-end: 1px solid var(--border);
  border-block-end: 1px solid var(--border);
}

.tooltip--below::after {
  inset-block-start: -5px;
  border-inline-start: 1px solid var(--border);
  border-block-start: 1px solid var(--border);
}

@keyframes tooltip-in {
  from {
    opacity: 0;
    scale: 0.96;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tooltip {
    animation: none;
  }
}
